const SKILL_LABEL_MAP: Record<string, string> = {
  dotnet: '.NET',
  'dot-net': '.NET',
  'c-plus-plus': 'C++',
  cplusplus: 'C++',
  'c-sharp': 'C#',
  csharp: 'C#',
  'f-sharp': 'F#',
  fsharp: 'F#',
  nodejs: 'Node.js',
  'node-js': 'Node.js',
  'next-js': 'Next.js',
  'nuxt-js': 'Nuxt.js',
  ci: 'CI',
  cd: 'CD',
  'ci-cd': 'CI/CD',
  cicd: 'CI/CD',
  'ui-ux': 'UI/UX',
  'ux-ui': 'UI/UX',
  'qa-qc': 'QA/QC',
  aws: 'AWS',
  gcp: 'GCP',
  sql: 'SQL',
  nosql: 'NoSQL',
  api: 'API',
  apis: 'APIs',
  graphql: 'GraphQL',
  rest: 'REST',
  restapi: 'REST API',
  oauth: 'OAuth',
  jwt: 'JWT',
  kubernetes: 'Kubernetes',
  kubernetesoperator: 'Kubernetes Operator',
  typescript: 'TypeScript',
  javascript: 'JavaScript',
  firebase: 'Firebase',
};

const SKILL_MATCH_ALIASES: Record<string, string[]> = {
  dotnet: ['dotnet', 'dot net', '.net', 'net framework', 'asp net'],
  'dot-net': ['dotnet', 'dot net', '.net', 'net framework', 'asp net'],
  'c-plus-plus': ['c plus plus', 'cplusplus', 'c++'],
  cplusplus: ['c plus plus', 'cplusplus', 'c++'],
  'c-sharp': ['c sharp', 'csharp', 'c#'],
  csharp: ['c sharp', 'csharp', 'c#'],
  'f-sharp': ['f sharp', 'fsharp', 'f#'],
  fsharp: ['f sharp', 'fsharp', 'f#'],
  nodejs: ['nodejs', 'node js', 'node.js'],
  'node-js': ['nodejs', 'node js', 'node.js'],
  'next-js': ['nextjs', 'next js', 'next.js'],
  'nuxt-js': ['nuxtjs', 'nuxt js', 'nuxt.js'],
  'ci-cd': ['ci cd', 'ci/cd', 'cicd', 'continuous integration', 'continuous delivery'],
  cicd: ['ci cd', 'ci/cd', 'cicd', 'continuous integration', 'continuous delivery'],
  'ui-ux': ['ui ux', 'ui/ux'],
  'qa-qc': ['qa qc', 'qa/qc'],
};

const UPPER_WORDS = new Set([
  'ai', 'ml', 'nlp', 'llm', 'api', 'sdk', 'sql', 'nosql', 'aws', 'gcp', 'ci', 'cd', 'qa', 'qc', 'ui', 'ux', 'etl',
]);

function canonicalSkillKey(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/\+/g, ' plus ')
    .replace(/#/g, ' sharp ')
    .replace(/[\u2019']/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function normalizeForSearch(value: string): string {
  return value
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/\+/g, ' plus ')
    .replace(/#/g, ' sharp ')
    .replace(/[\u2019']/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function titleWord(word: string): string {
  if (!word) return word;
  if (UPPER_WORDS.has(word)) return word.toUpperCase();
  if (word.length <= 2 && /^[a-z]+$/.test(word)) return word.toUpperCase();
  return word.charAt(0).toUpperCase() + word.slice(1);
}

export function formatSkillLabel(skill: string): string {
  const raw = (skill || '').trim();
  if (!raw) return raw;

  const key = canonicalSkillKey(raw);
  if (!key) return raw;

  if (SKILL_LABEL_MAP[key]) {
    return SKILL_LABEL_MAP[key];
  }

  const words = key.split('-').filter(Boolean);
  if (words.length === 0) return raw;
  return words.map(titleWord).join(' ');
}

export function isSkillExplicitInResume(skill: string, resumeText: string): boolean {
  const key = canonicalSkillKey(skill);
  if (!key) return false;

  const normalizedResume = ` ${normalizeForSearch(resumeText || '')} `;
  const compactResume = normalizedResume.replace(/\s+/g, '');
  if (!normalizedResume.trim()) return false;

  const variants = new Set<string>();
  variants.add(normalizeForSearch(skill));
  variants.add(normalizeForSearch(key.replace(/-/g, ' ')));
  variants.add(normalizeForSearch(formatSkillLabel(skill)));
  (SKILL_MATCH_ALIASES[key] || []).forEach(v => variants.add(normalizeForSearch(v)));

  for (const variant of Array.from(variants)) {
    if (!variant) continue;
    if (normalizedResume.includes(` ${variant} `)) return true;
    const compactVariant = variant.replace(/\s+/g, '');
    if (compactVariant.length >= 4 && compactResume.includes(compactVariant)) return true;
  }

  return false;
}